import { medicalList, allergyList, shortsAndTshirtSizes, servingService, ageArr } from '../helper';

// String-based question types (richer than the numeric enum used by the unrelated
// program-booking-questions feature in AddProgramModal.js — this form needs types
// that enum doesn't have, e.g. date/email/tel/file/signature/info).
export const QUESTION_TYPES = {
    TEXT: 'text',
    EMAIL: 'email',
    TEL: 'tel',
    NUMBER: 'number',
    DATE: 'date',
    SELECT: 'select',
    TEXTAREA: 'textarea',
    CHECKBOX: 'checkbox',
    YESNO: 'yesno',
    FILE: 'file',
    CHECKLIST: 'checklist',
    SIGNATURE: 'signature',
    INFO: 'info',
};

// questionKey values for locked questions match the existing addCounselor.js state/field
// names 1:1 so the fixed FormData submission contract stays byte-identical to today.
// Only the non-locked `userDescription` (bio) question is director-editable in v1; every
// other default question is locked because it maps directly to a real backend field.
export const DEFAULT_STAFF_FORM_SCHEMA = [
    {
        sectionKey: 'applicant',
        title: 'Applicant Details',
        description: 'Tell us who you are and how we can reach you.',
        icon: 'user',
        questions: [
            { questionKey: 'userTitle', label: 'Title', type: QUESTION_TYPES.TEXT, placeholder: 'Enter title', width: 'half', required: false, enabled: true, locked: true },
            { questionKey: 'serving', label: 'How are you serving?', type: QUESTION_TYPES.SELECT, options: servingService, placeholder: 'Select serving', width: 'half', required: false, enabled: true, locked: true },
            { questionKey: 'firstName', label: 'First name', type: QUESTION_TYPES.TEXT, placeholder: 'Enter first name', width: 'half', required: true, enabled: true, locked: true },
            { questionKey: 'lastName', label: 'Last name', type: QUESTION_TYPES.TEXT, placeholder: 'Enter last name', width: 'half', required: true, enabled: true, locked: true },
            { questionKey: 'phone', label: 'Phone number', type: QUESTION_TYPES.TEL, width: 'half', required: true, enabled: true, locked: true },
            { questionKey: 'email', label: 'Email', type: QUESTION_TYPES.EMAIL, placeholder: 'Enter email address', width: 'half', required: true, enabled: true, locked: true },
            { questionKey: 'streetAddress', label: 'Street address', type: QUESTION_TYPES.TEXT, placeholder: 'Street address', width: 'half', required: true, enabled: true, locked: true },
            { questionKey: 'appartment', label: 'Apt. / Suite / Unit', type: QUESTION_TYPES.TEXT, placeholder: 'Apt. / Suite / Unit', width: 'half', required: false, enabled: true, locked: true },
            { questionKey: 'city', label: 'City', type: QUESTION_TYPES.TEXT, placeholder: 'City', width: 'half', required: true, enabled: true, locked: true },
            { questionKey: 'zipCode', label: 'Zip code', type: QUESTION_TYPES.TEXT, placeholder: 'Zip code', width: 'half', required: true, enabled: true, locked: true },
            { questionKey: 'state', label: 'State / Province', type: QUESTION_TYPES.TEXT, placeholder: 'State / Province', width: 'half', required: true, enabled: true, locked: true },
            { questionKey: 'dob', label: 'Date of birth', type: QUESTION_TYPES.DATE, width: 'half', required: true, enabled: true, locked: true },
        ],
    },
    // identity / experience / references below ship with no questions on purpose: the client
    // asked for the headings to exist so a director can add their own questions inside them, and
    // none of them maps to a backend field the way the locked questions elsewhere in this file
    // do. Everything a director adds to those is a custom question, stored in `answers`.
    //
    // An empty section is invisible to applicants — addCounselorNew's `visibleSections` drops
    // any section with no enabled question — so it appears on the application only once a
    // director has put something in it.
    {
        sectionKey: 'identity',
        title: 'Identification & Background Check',
        description: 'Required for employment. Your application cannot be processed without accurate identification.',
        icon: 'shield',
        questions: [],
    },
    {
        sectionKey: 'sessions',
        title: 'Programs & Availability',
        description: 'Tell us which programs you are available to work. You can update this later with your director.',
        icon: 'calendar',
        // Program name and Age group are optional, hideable, User-backed system fields (not
        // custom `answers`). They live here by default rather than in Applicant Details because
        // they are about placement, not contact info. A director can hide either from the Form
        // Builder. Existing camps keep them under Applicant Details — this default only applies
        // to a form created from scratch.
        questions: [
            { questionKey: 'counselorProgram', label: 'Program name', type: QUESTION_TYPES.SELECT, optionsSource: 'campPrograms', placeholder: 'Select program name', width: 'half', required: false, enabled: true, locked: true },
            {
                questionKey: 'ageGroup', label: 'Age group', type: QUESTION_TYPES.SELECT, options: ageArr, placeholder: 'Select age', width: 'half', required: false, enabled: true, locked: true,
                helpText: 'This is the age group the personnel will be instructing, simply choose the best/most representative one',
            },
        ],
    },
    {
        sectionKey: 'experience',
        title: 'Staff Experience',
        description: 'Your background helps us place you with the right group and position.',
        icon: 'whistle',
        questions: [],
    },
    {
        sectionKey: 'apparel',
        title: 'Apparel',
        description: 'Staff gear is sized before camp begins.',
        icon: 'shirt',
        questions: [
            { questionKey: 'tshirtSize', label: 'T-shirt size', type: QUESTION_TYPES.SELECT, options: shortsAndTshirtSizes, placeholder: 'Select size', width: 'half', required: false, enabled: true, locked: true },
            { questionKey: 'shortsSize', label: 'Shorts size', type: QUESTION_TYPES.SELECT, options: shortsAndTshirtSizes, placeholder: 'Select size', width: 'half', required: false, enabled: true, locked: true },
        ],
    },
    {
        sectionKey: 'references',
        title: 'References',
        description: 'We may contact your references before confirming your placement.',
        icon: 'users',
        questions: [],
    },
    {
        sectionKey: 'medical',
        title: 'Medical & Emergency',
        description: 'Kept confidential and shared only with on-site staff.',
        icon: 'heart',
        questions: [
            { questionKey: 'medicalvalues', label: 'Medical', type: QUESTION_TYPES.CHECKLIST, options: medicalList, width: 'full', required: false, enabled: true, locked: true },
            { questionKey: 'allergyvalues', label: 'Allergy', type: QUESTION_TYPES.CHECKLIST, options: allergyList, width: 'full', required: false, enabled: true, locked: true },
            { questionKey: 'allergyDeadly', label: 'Allergy is deadly', type: QUESTION_TYPES.YESNO, width: 'full', required: false, enabled: true, locked: true },
            { questionKey: 'medicalInsuranceCompany', label: 'Medical insurance company', type: QUESTION_TYPES.TEXT, placeholder: 'Medical insurance company', width: 'half', required: false, enabled: true, locked: true },
            { questionKey: 'policyNumber', label: 'Policy number', type: QUESTION_TYPES.TEXT, placeholder: 'Policy number', width: 'half', required: false, enabled: true, locked: true },
            { questionKey: 'group', label: 'Group #', type: QUESTION_TYPES.TEXT, placeholder: 'Group #', width: 'half', required: false, enabled: true, locked: true },
            { questionKey: 'primaryPhysician', label: 'Primary physician', type: QUESTION_TYPES.TEXT, placeholder: 'Primary physician', width: 'half', required: false, enabled: true, locked: true },
            { questionKey: 'physicianAddress', label: 'Physician address', type: QUESTION_TYPES.TEXT, placeholder: 'Physician address', width: 'half', required: false, enabled: true, locked: true },
            { questionKey: 'physicianPhone', label: 'Physician phone #', type: QUESTION_TYPES.TEL, width: 'half', required: false, enabled: true, locked: true },
            {
                questionKey: 'medicationsTaken', label: 'Please list any medications taken (please include dosage & timing) or any other special assistance that may be required', type: QUESTION_TYPES.TEXTAREA,
                placeholder: 'Write something here… (leave blank if none)', width: 'full', required: false, enabled: true, locked: true,
            },
            { questionKey: 'participantBringingOwnMedication', label: 'Will you be bringing your own medication to a program?', type: QUESTION_TYPES.YESNO, width: 'full', required: false, enabled: true, locked: true },
            { questionKey: 'allowDocumentCamper', label: 'I may be included in digital and film photography and/or video while on site', type: QUESTION_TYPES.YESNO, width: 'full', required: false, enabled: true, locked: true },
            {
                questionKey: 'medicalDocument', label: 'Please attach any accompanying documents that you feel are necessary for you to serve (additional medical documents, etc.)', type: QUESTION_TYPES.FILE,
                width: 'full', required: false, enabled: true, locked: true,
            },
        ],
    },
    {
        sectionKey: 'documents',
        title: 'Documents & Media',
        description: 'A little about you.',
        icon: 'paperclip',
        questions: [
            // Locked despite being editable-looking: the bio is sent as a top-level
            // `userDescription` field and inside `counselorDetails`, and User.userDescription
            // is what the public camp-detail staff bios render. Routing it through `answers`
            // instead would blank those pages, and leaving it unlocked would store it twice.
            {
                questionKey: 'userDescription', label: 'Personal bio', type: QUESTION_TYPES.TEXTAREA,
                placeholder: 'Write description/short bio here of the new personnel, who they will be serving, passions etc.',
                width: 'full', required: false, enabled: true, locked: true,
            },
        ],
    },
    {
        sectionKey: 'agreements',
        title: 'Waivers & Agreements',
        description: "Review your camp's agreement, then confirm below.",
        icon: 'signature',
        questions: [
            // Rendered as a dynamic download link sourced from the camp's trainingGuidePdf — no static body.
            { questionKey: 'trainingGuidePdf', label: 'Training guide', type: QUESTION_TYPES.INFO, tone: 'neutral', width: 'full', required: false, enabled: true, locked: true },
            // Rendered as a gated checkbox that opens the camp's agreement text in a modal; checking it
            // requires clicking "I agree" inside that modal (see StaffFormBuilder special-case notes).
            { questionKey: 'readTrainingGuide', label: 'Agreement', type: QUESTION_TYPES.CHECKBOX, width: 'full', required: true, enabled: true, locked: true },
            // Rendered via SignatureField (signature pad modal) — combines the signIsLegal flag and signatureImg.
            { questionKey: 'signature', label: 'Agreement signature', type: QUESTION_TYPES.SIGNATURE, width: 'full', required: true, enabled: true, locked: true },
        ],
    },
];

// Questions the Form Builder's eye icon is greyed out for. Distinct from `locked`, which marks
// a system field as undeletable: a locked question can still be hidden, which keeps the backend
// field intact and only stops the applicant being asked for it. What survives here is the
// minimum that makes an applicant record usable — a name, a way to reach them, an address — plus
// the two agreement items that make the application binding.
//
// Mirrors NON_HIDEABLE_QUESTION_KEYS in the API's staffform/staffFormConstants.js, which is what
// actually enforces this. Keep the two in sync: if this list is the shorter one, the eye icon
// renders enabled and the toggle then fails with an unauthorized error.
export const NON_HIDEABLE_QUESTION_KEYS = [
    'firstName', 'lastName', 'phone', 'email', 'streetAddress', 'appartment',
    'city', 'zipCode', 'state', 'dob',
    'readTrainingGuide', 'signature',
];

export const isQuestionHideable = question => !NON_HIDEABLE_QUESTION_KEYS.includes(question.questionKey);
